import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks/reduxHooks';
import { fetchDashboardStats } from '../../features/dashboard/dashboardSlice';
import { UsersIcon, ShoppingCartIcon, UserGroupIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';

interface DetailItem {
  label: string;
  value: string;
  color?: string;
}

interface EnhancedStatCardProps {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon?: React.ReactNode;
  details?: DetailItem[];
  className?: string;
  isLoading?: boolean;
}

const EnhancedStatCard: React.FC<EnhancedStatCardProps> = ({
  title, value, change, changeType = 'neutral', icon, details = [], className = '', isLoading = false
}) => {
  const changeColorClass = {
    positive: 'text-green-500',
    negative: 'text-red-500',
    neutral: 'text-brand-gray',
  }[changeType];

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm p-6 ${className} animate-pulse`}>
        <div className="flex justify-between">
          <div>
            <div className="h-5 bg-gray-200 rounded w-24 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-16"></div>
          </div>
          <div className="w-12 h-12 rounded-full bg-gray-200"></div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 pt-3 border-t border-gray-100">
          {[1, 2, 3].map((_, index) => (
            <div key={index} className="text-center">
              <div className="h-3 bg-gray-200 rounded w-12 mx-auto mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-10 mx-auto"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm p-6 ${className}`}>
      <div className="flex justify-between">
        <div>
          <h3 className="text-lg font-gilroy-semibold mb-1 text-gray-600">{title}</h3>
          <p className="text-3xl font-gilroy-bold text-brand-blue">{value}</p>
          {change && (
            <p className={`text-sm mt-1 ${changeColorClass}`}>
              {changeType === 'positive' ? '+' : changeType === 'negative' ? '-' : ''}{change}
            </p>
          )}
        </div>
        {icon && (
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-500">{icon}</div>
        )}
      </div>
      {details.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-2 pt-3 border-t border-gray-100">
          {details.map((detail, index) => (
            <div key={index} className="text-center">
              <p className="text-xs text-gray-500 mb-1">{detail.label}</p>
              <p className={`text-sm font-gilroy-semibold ${detail.color || 'text-gray-700'}`}>{detail.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DashboardStats: React.FC = () => {
  const dispatch = useAppDispatch();
  const { merchantStats, partnerStats, orderStats, customerStats, loading, hasFetched } = useAppSelector(state => state.dashboard);

  useEffect(() => {
    if (!hasFetched && !loading) {
      dispatch(fetchDashboardStats());
    }
  }, [dispatch, hasFetched, loading]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <EnhancedStatCard
        title="Total Merchants"
        value={merchantStats?.totalMerchants || 0}
        icon={<BuildingStorefrontIcon className="w-6 h-6" />}
        isLoading={loading}
        details={merchantStats ? [
          { label: "Active", value: `${merchantStats.activeMerchants.count} (${merchantStats.activeMerchants.percentage}%)`, color: "text-green-500" },
          { label: "Inactive", value: `${merchantStats.inactiveMerchants.count} (${merchantStats.inactiveMerchants.percentage}%)`, color: "text-gray-500" },
          { label: "KYC Pending", value: `${merchantStats.kycPendingMerchants.count} (${merchantStats.kycPendingMerchants.percentage}%)`, color: "text-orange-500" },
        ] : []}
      />

      <EnhancedStatCard
        title="Total Partners"
        value={partnerStats?.totalPartners || 0}
        icon={<UserGroupIcon className="w-6 h-6" />}
        isLoading={loading}
        details={partnerStats ? [
          { label: "EV Partners", value: `${partnerStats.evPartners.count} (${partnerStats.evPartners.percentage}%)`, color: "text-blue-500" },
          { label: "BNPL Partners", value: `${partnerStats.bnplPartners.count} (${partnerStats.bnplPartners.percentage}%)`, color: "text-purple-500" },
          { label: "Satisfaction", value: "95%", color: "text-green-500" },
        ] : []}
      />

      <EnhancedStatCard
        title="Completed Orders"
        value={orderStats?.completedOrdersCount?.toLocaleString() || 0}
        icon={<ShoppingCartIcon className="w-6 h-6" />}
        isLoading={loading}
        details={orderStats ? [
          { label: "Today", value: orderStats.completedTodayCount.toString(), color: "text-brand-blue" },
          { label: "This Week", value: orderStats.completedThisWeekCount.toString(), color: "text-brand-blue" },
          { label: "Conversion", value: orderStats.conversion.rate, color: "text-green-500" },
        ] : []}
      />

      <EnhancedStatCard
        title="Total Customers"
        value={customerStats?.total?.toLocaleString() || 0}
        icon={<UsersIcon className="w-6 h-6" />}
        isLoading={loading}
        details={customerStats ? [
          { label: "New Today", value: customerStats.today.toString(), color: "text-brand-blue" },
          { label: "This Week", value: customerStats.thisWeek.toString(), color: "text-brand-blue" },
          { label: "This Month", value: customerStats.thisMonth.toString(), color: "text-brand-blue" },
        ] : []}
      />
    </div>
  );
};

export default DashboardStats;
